import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Badge,
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";
import { END_POINT } from "../../../constants/defaultValues";
import {
  AddCommaNumber,
  NotificatioErrController,
  NotificatioSucController,
} from "../../../helpers/Utils";

const DEFAULT_PAGE_SIZE = 8;

const emptyHistoryData = {
  stock: null,
  summary: {
    import_count: 0,
    total_import_quantity: 0,
    total_import_value: 0,
  },
  logs: [],
  totalItem: 0,
  totalPage: 0,
  currentPage: 1,
  pageSize: DEFAULT_PAGE_SIZE,
};

const toNumber = (value) => {
  const numberValue = Number(String(value || "").replace(/\./g, "").replace(/,/g, ""));
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const formatCurrency = (value) => `${AddCommaNumber(Math.round(Number(value || 0)))} đ`;

const formatQuantity = (value, unit) =>
  `${AddCommaNumber(Number(value || 0))}${unit ? ` ${unit}` : ""}`;

const buildEditForm = (log) => ({
  count: log ? String(log.quantity || "") : "",
  unit_price: log ? String(log.unit_price || "") : "",
  date: log ? log.date || "" : "",
  time: log ? log.time || "" : "",
  expiry_date: log ? log.expiry_date || "" : "",
  edit_reason: "",
});

const StockImportHistoryModal = ({
  isOpen,
  toggle,
  stock,
  canUpdate,
  onSaved,
}) => {
  const [historyData, setHistoryData] = useState(emptyHistoryData);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({ date_start: "", date_end: "" });
  const [editLogId, setEditLogId] = useState(null);
  const [editForm, setEditForm] = useState(buildEditForm(null));

  const stockId = stock && stock.id;
  const selectedStock = historyData.stock || stock || {};
  const editingLog = useMemo(
    () => (historyData.logs || []).find((item) => item.id === editLogId),
    [historyData.logs, editLogId],
  );

  const preview = useMemo(() => {
    if (!editingLog) return null;
    const nextQuantity = toNumber(editForm.count);
    const nextUnitPrice = toNumber(editForm.unit_price);
    const oldQuantity = Number(editingLog.quantity || 0);
    const oldUnitPrice = Number(editingLog.unit_price || 0);
    return {
      quantity: nextQuantity - oldQuantity,
      value: nextQuantity * nextUnitPrice - oldQuantity * oldUnitPrice,
    };
  }, [editingLog, editForm.count, editForm.unit_price]);

  const fetchHistory = (page = currentPage, nextFilters = filters) => {
    if (!isOpen || !stock || !stock.id) return;
    const tokenStr = localStorage.getItem("access_token");
    const query = [
      `pageSize=${DEFAULT_PAGE_SIZE}`,
      `currentPage=${page}`,
      `date_start=${encodeURIComponent(nextFilters.date_start || "")}`,
      `date_end=${encodeURIComponent(nextFilters.date_end || "")}`,
    ].join("&");

    setLoading(true);
    axios
      .get(`${END_POINT}/stock/${stock.id}/import-logs?${query}`, {
        headers: { Authorization: `Bearer ${tokenStr}` },
      })
      .then((res) => res.data)
      .then((result) => {
        setHistoryData(result.data || emptyHistoryData);
        setCurrentPage(result.data?.currentPage || page);
      })
      .catch((err) => {
        NotificatioErrController(
          err.response?.data?.responseText || "Không thể tải lịch sử nhập",
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (!isOpen) return;
    setHistoryData(emptyHistoryData);
    setCurrentPage(1);
    setEditLogId(null);
    setEditForm(buildEditForm(null));
  }, [isOpen, stockId]);

  useEffect(() => {
    if (!isOpen || !stock || !stock.id) return;
    fetchHistory(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, stockId, currentPage]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    setCurrentPage(1);
    fetchHistory(1);
  };

  const resetFilters = () => {
    const nextFilters = { date_start: "", date_end: "" };
    setFilters(nextFilters);
    setCurrentPage(1);
    fetchHistory(1, nextFilters);
  };

  const startEdit = (log) => {
    setEditLogId(log.id);
    setEditForm(buildEditForm(log));
  };

  const cancelEdit = () => {
    setEditLogId(null);
    setEditForm(buildEditForm(null));
  };

  const handleEditChange = (event) => {
    const { name, value } = event.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const saveEdit = () => {
    if (!stock || !stock.id || !editLogId) return;
    if (toNumber(editForm.count) <= 0) {
      NotificatioErrController("Số lượng nhập phải lớn hơn 0");
      return;
    }
    if (toNumber(editForm.unit_price) <= 0) {
      NotificatioErrController("Đơn giá nhập phải lớn hơn 0");
      return;
    }
    if (!editForm.date || !editForm.time) {
      NotificatioErrController("Ngày và giờ nhập không được để trống");
      return;
    }
    if (!editForm.edit_reason || !editForm.edit_reason.trim()) {
      NotificatioErrController("Vui lòng nhập lý do chỉnh sửa");
      return;
    }

    const tokenStr = localStorage.getItem("access_token");
    setSaving(true);
    axios
      .put(
        `${END_POINT}/stock/${stock.id}/import-logs/${editLogId}`,
        {
          count: toNumber(editForm.count),
          unit_price: toNumber(editForm.unit_price),
          date: editForm.date,
          time: editForm.time,
          expiry_date: editForm.expiry_date || null,
          edit_reason: editForm.edit_reason,
        },
        {
          headers: { Authorization: `Bearer ${tokenStr}` },
        },
      )
      .then((res) => res.data)
      .then(() => {
        NotificatioSucController("Cập nhật lần nhập thành công");
        cancelEdit();
        fetchHistory(currentPage);
        if (onSaved) onSaved();
      })
      .catch((err) => {
        NotificatioErrController(
          err.response?.data?.responseText || "Có lỗi xảy ra khi cập nhật",
        );
      })
      .finally(() => {
        setSaving(false);
      });
  };

  const summary = historyData.summary || emptyHistoryData.summary;
  const logs = historyData.logs || [];
  const totalPage = historyData.totalPage || 0;

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      className="table-modal max-width-100"
      size="xl"
    >
      <ModalHeader toggle={toggle}>Lịch sử nhập nguyên liệu</ModalHeader>
      <ModalBody>
        <div className="d-flex flex-wrap justify-content-between align-items-start mb-3">
          <div className="mb-2">
            <h5 className="mb-1">{selectedStock.name || "-"}</h5>
            <div className="text-muted">
              {selectedStock.sku || "-"} · Tồn kho:{" "}
              {formatQuantity(selectedStock.count, selectedStock.unit)}
            </div>
          </div>
          <Badge color="primary" pill>
            {formatCurrency(selectedStock.total_value || 0)}
          </Badge>
        </div>

        <Row className="mb-3">
          <Col md="4" className="mb-2">
            <div className="border rounded p-3 h-100">
              <small className="text-muted d-block">Số lần nhập</small>
              <strong>{AddCommaNumber(summary.import_count || 0)}</strong>
            </div>
          </Col>
          <Col md="4" className="mb-2">
            <div className="border rounded p-3 h-100">
              <small className="text-muted d-block">Tổng số lượng nhập</small>
              <strong>
                {formatQuantity(summary.total_import_quantity, selectedStock.unit)}
              </strong>
            </div>
          </Col>
          <Col md="4" className="mb-2">
            <div className="border rounded p-3 h-100">
              <small className="text-muted d-block">Tổng giá trị nhập</small>
              <strong>{formatCurrency(summary.total_import_value)}</strong>
            </div>
          </Col>
        </Row>

        <div className="d-flex flex-wrap align-items-end mb-3">
          <FormGroup className="mr-2 mb-2">
            <Label className="text-muted">Từ ngày</Label>
            <Input
              type="date"
              name="date_start"
              value={filters.date_start}
              onChange={handleFilterChange}
            />
          </FormGroup>
          <FormGroup className="mr-2 mb-2">
            <Label className="text-muted">Đến ngày</Label>
            <Input
              type="date"
              name="date_end"
              value={filters.date_end}
              onChange={handleFilterChange}
            />
          </FormGroup>
          <Button
            color="outline-primary"
            className="mr-2 mb-2"
            onClick={applyFilters}
            disabled={loading}
          >
            Lọc
          </Button>
          <Button
            color="outline-secondary"
            className="mb-2"
            onClick={resetFilters}
            disabled={loading}
          >
            Xóa lọc
          </Button>
        </div>

        <div className="table-responsive">
          <Table hover className="mb-0">
            <thead>
              <tr>
                <th>Ngày giờ</th>
                <th>Nguồn</th>
                <th className="text-right">Số lượng</th>
                <th className="text-right">Đơn giá</th>
                <th className="text-right">Thành tiền</th>
                <th>Hạn dùng</th>
                <th>Trạng thái</th>
                <th className="text-center">Sửa</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center text-muted py-4">
                    Đang tải...
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center text-muted py-4">
                    Chưa có dữ liệu
                  </td>
                </tr>
              ) : (
                logs.map((log) => {
                  const isEditing = editLogId === log.id;
                  return (
                    <React.Fragment key={log.id}>
                      <tr>
                        <td>
                          <div>{log.date || "-"}</div>
                          <small className="text-muted">{log.time || "-"}</small>
                        </td>
                        <td>
                          <Badge
                            color={
                              log.source_type === "legacy_stock_log"
                                ? "secondary"
                                : "info"
                            }
                            pill
                          >
                            {log.source_label || "Nhập kho"}
                          </Badge>
                        </td>
                        <td className="text-right">
                          {formatQuantity(log.quantity, selectedStock.unit)}
                        </td>
                        <td className="text-right">
                          {formatCurrency(log.unit_price)}
                        </td>
                        <td className="text-right">
                          {formatCurrency(log.total_value)}
                        </td>
                        <td>{log.expiry_date || "-"}</td>
                        <td>
                          {log.is_edited ? (
                            <Badge color="warning" pill>
                              Đã sửa
                            </Badge>
                          ) : (
                            <Badge color="light" pill>
                              Gốc
                            </Badge>
                          )}
                        </td>
                        <td className="text-center">
                          {canUpdate && !["stock_replenishment", "warehouse_import"].includes(log.source_type) ? (
                            <Button
                              color="link"
                              className="p-0"
                              onClick={() => startEdit(log)}
                            >
                              Sửa
                            </Button>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                      {isEditing && (
                        <tr>
                          <td colSpan="8" className="bg-light">
                            <Row>
                              <Col md="2" className="mb-2">
                                <Label className="text-muted">Số lượng</Label>
                                <Input
                                  type="number"
                                  step="any"
                                  name="count"
                                  value={editForm.count}
                                  onChange={handleEditChange}
                                />
                              </Col>
                              <Col md="2" className="mb-2">
                                <Label className="text-muted">Đơn giá</Label>
                                <Input
                                  name="unit_price"
                                  value={AddCommaNumber(editForm.unit_price)}
                                  onChange={(event) =>
                                    setEditForm((prev) => ({
                                      ...prev,
                                      unit_price: event.target.value.replace(/\./g, ""),
                                    }))
                                  }
                                />
                              </Col>
                              <Col md="2" className="mb-2">
                                <Label className="text-muted">Ngày</Label>
                                <Input
                                  type="date"
                                  name="date"
                                  value={editForm.date}
                                  onChange={handleEditChange}
                                />
                              </Col>
                              <Col md="2" className="mb-2">
                                <Label className="text-muted">Giờ</Label>
                                <Input
                                  type="time"
                                  name="time"
                                  value={editForm.time}
                                  onChange={handleEditChange}
                                />
                              </Col>
                              <Col md="2" className="mb-2">
                                <Label className="text-muted">Hạn dùng</Label>
                                <Input
                                  type="date"
                                  name="expiry_date"
                                  value={editForm.expiry_date || ""}
                                  onChange={handleEditChange}
                                />
                              </Col>
                              <Col md="2" className="mb-2">
                                <Label className="text-muted">Chênh lệch</Label>
                                <div>
                                  <div>
                                    {preview
                                      ? formatQuantity(preview.quantity, selectedStock.unit)
                                      : "-"}
                                  </div>
                                  <small className="text-muted">
                                    {preview ? formatCurrency(preview.value) : "-"}
                                  </small>
                                </div>
                              </Col>
                            </Row>
                            <FormGroup className="mb-2">
                              <Label className="text-muted">Lý do chỉnh sửa</Label>
                              <Input
                                type="textarea"
                                name="edit_reason"
                                rows="2"
                                value={editForm.edit_reason}
                                onChange={handleEditChange}
                              />
                            </FormGroup>
                            <div className="d-flex justify-content-end">
                              <Button
                                color="outline-secondary"
                                className="mr-2"
                                onClick={cancelEdit}
                                disabled={saving}
                              >
                                Hủy
                              </Button>
                              <Button color="primary" onClick={saveEdit} disabled={saving}>
                                {saving ? "Đang lưu..." : "Lưu chỉnh sửa"}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>
      </ModalBody>
      <ModalFooter className="d-flex justify-content-between">
        <div className="text-muted">
          Trang {totalPage ? currentPage : 0}/{totalPage || 0}
        </div>
        <div>
          <Button
            color="outline-secondary"
            className="mr-2"
            disabled={loading || currentPage <= 1}
            onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
          >
            Trước
          </Button>
          <Button
            color="outline-secondary"
            disabled={loading || !totalPage || currentPage >= totalPage}
            onClick={() => setCurrentPage((page) => page + 1)}
          >
            Sau
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default StockImportHistoryModal;
